import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  Printer,
  Search,
  QrCode,
  Download,
  RefreshCw,
  ChevronDown,
  Settings,
  Grid,
  List,
  CheckCircle,
  X,
  Smartphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

// Label Component (Printable)
const DeviceLabel = ({ device, config, showBorder }) => {
  const { width, height, showQR, showBin, showIMEI, showGrade, fontSize } = config;
  
  return (
    <div 
      className={`bg-white p-3 ${showBorder ? 'border border-gray-300' : ''}`}
      style={{ 
        width: `${width}mm`, 
        height: `${height}mm`,
        pageBreakAfter: 'always',
        pageBreakInside: 'avoid'
      }}
    >
      <div className="h-full flex flex-col justify-between">
        {/* Header */}
        <div className="text-center border-b border-gray-200 pb-1">
          <p className="font-bold text-gray-800" style={{ fontSize: `${fontSize}px` }}>
            {device.device_id}
          </p>
        </div>
        
        {/* Content */}
        <div className="flex gap-2 flex-1 py-2">
          {/* QR Code */}
          {showQR && device.qr_code && (
            <div className="flex-shrink-0">
              <img 
                src={`data:image/png;base64,${device.qr_code}`} 
                alt="DID QR" 
                style={{ width: `${Math.min(width * 0.4, 25)}mm`, height: `${Math.min(width * 0.4, 25)}mm` }}
              />
            </div>
          )}
          
          {/* Details */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800 truncate" style={{ fontSize: `${fontSize - 1}px` }}>
              {device.brand} {device.model}
            </p>
            {device.variant && (
              <p className="text-gray-500 truncate" style={{ fontSize: `${fontSize - 2}px` }}>
                {device.variant} {device.storage}
              </p>
            )}
            {showIMEI && device.imei && (
              <p className="text-gray-400 font-mono truncate" style={{ fontSize: `${fontSize - 3}px` }}>
                IMEI: {device.imei}
              </p>
            )}
            {showGrade && device.pq_grade && (
              <p className="font-semibold text-[#2b3380]" style={{ fontSize: `${fontSize - 1}px` }}>
                Grade: {device.pq_grade}
              </p>
            )}
          </div>
        </div>
        
        {/* Bin Location */}
        {showBin && device.bin_id && (
          <div className="border-t border-gray-200 pt-1 flex items-center justify-between">
            <span className="text-gray-500" style={{ fontSize: `${fontSize - 2}px` }}>BIN:</span>
            <span className="font-bold text-gray-800" style={{ fontSize: `${fontSize}px` }}>
              {device.bin_id}
            </span>
            {device.bin_qr && (
              <img 
                src={`data:image/png;base64,${device.bin_qr}`} 
                alt="Bin QR" 
                style={{ width: '15mm', height: '15mm' }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function DIDLabelPrinting({ user, onLogout }) {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showSettings, setShowSettings] = useState(false);
  const printRef = useRef(null);
  
  // Label config
  const [labelConfig, setLabelConfig] = useState({
    width: 50,  // mm
    height: 30, // mm
    showQR: true,
    showBin: true,
    showIMEI: true,
    showGrade: true,
    fontSize: 10,
    labelsPerRow: 3
  });

  useEffect(() => {
    fetchDevices();
    loadLabelConfig();
    
    // Check for specific DID in URL
    const did = searchParams.get('did');
    if (did) {
      setSelectedDevices([did]);
    }
  }, [searchParams]);

  const loadLabelConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/masters/label-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.config) {
          setLabelConfig(prev => ({ ...prev, ...data.config }));
        }
      }
    } catch (error) {
      console.log('Using default label config');
    }
  };

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get devices that need labels (DID generated but not yet printed, or recent ones)
      const response = await fetch(`${API_URL}/api/warehouse/devices/iqc-pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Fetch full label data for each device
        const devicesWithLabels = await Promise.all(
          (data.devices || []).map(async (d) => {
            try {
              const labelRes = await fetch(`${API_URL}/api/warehouse/did/${d.device_id}/label`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (labelRes.ok) {
                const labelData = await labelRes.json();
                return { ...d, ...labelData.label };
              }
            } catch (e) {}
            return d;
          })
        );
        
        setDevices(devicesWithLabels);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDevice = (deviceId) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const selectAll = () => {
    setSelectedDevices(filteredDevices.map(d => d.device_id));
  };

  const clearSelection = () => {
    setSelectedDevices([]);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>DID Labels</title>
          <style>
            @page { size: ${labelConfig.width}mm ${labelConfig.height}mm; margin: 0; }
            body { margin: 0; padding: 0; }
            .label { 
              width: ${labelConfig.width}mm; 
              height: ${labelConfig.height}mm; 
              padding: 2mm;
              box-sizing: border-box;
              page-break-after: always;
              font-family: Arial, sans-serif;
            }
            .label:last-child { page-break-after: auto; }
            .qr-code { max-width: 20mm; max-height: 20mm; }
            .device-id { font-weight: bold; font-size: ${labelConfig.fontSize}px; }
            .model { font-size: ${labelConfig.fontSize - 1}px; }
            .details { font-size: ${labelConfig.fontSize - 2}px; color: #666; }
          </style>
        </head>
        <body>
          ${selectedDevices.map(did => {
            const device = devices.find(d => d.device_id === did);
            if (!device) return '';
            return `
              <div class="label">
                <div class="device-id">${device.device_id}</div>
                <div style="display: flex; gap: 5mm; margin-top: 2mm;">
                  ${labelConfig.showQR && device.qr_code ? `<img src="data:image/png;base64,${device.qr_code}" class="qr-code" />` : ''}
                  <div>
                    <div class="model">${device.brand} ${device.model}</div>
                    ${device.variant ? `<div class="details">${device.variant} ${device.storage || ''}</div>` : ''}
                    ${labelConfig.showIMEI && device.imei ? `<div class="details">IMEI: ${device.imei}</div>` : ''}
                    ${labelConfig.showGrade && device.pq_grade ? `<div class="model">Grade: ${device.pq_grade}</div>` : ''}
                  </div>
                </div>
                ${labelConfig.showBin && device.bin_id ? `<div class="details" style="margin-top: 2mm;">BIN: ${device.bin_id}</div>` : ''}
              </div>
            `;
          }).join('')}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const filteredDevices = devices.filter(d => 
    !searchQuery || 
    d.device_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.brand?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.model?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">DID Label Printing</h1>
            <p className="text-sm text-gray-500">Generate and print device identification labels</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 text-gray-500 hover:text-[#2b3380] hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              onClick={fetchDevices}
              className="p-2 text-gray-500 hover:text-[#2b3380] hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6">
            <h3 className="font-semibold text-gray-800 mb-4">Label Settings</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Width (mm)</label>
                <input
                  type="number"
                  value={labelConfig.width}
                  onChange={(e) => setLabelConfig(prev => ({ ...prev, width: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Height (mm)</label>
                <input
                  type="number"
                  value={labelConfig.height}
                  onChange={(e) => setLabelConfig(prev => ({ ...prev, height: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Font Size (px)</label>
                <input
                  type="number"
                  value={labelConfig.fontSize}
                  onChange={(e) => setLabelConfig(prev => ({ ...prev, fontSize: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Labels per Row</label>
                <input
                  type="number"
                  value={labelConfig.labelsPerRow}
                  onChange={(e) => setLabelConfig(prev => ({ ...prev, labelsPerRow: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showQR"
                  checked={labelConfig.showQR}
                  onChange={(e) => setLabelConfig(prev => ({ ...prev, showQR: e.target.checked }))}
                  className="w-4 h-4 text-[#2b3380]"
                />
                <label htmlFor="showQR" className="text-sm text-gray-600">Show QR Code</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showBin"
                  checked={labelConfig.showBin}
                  onChange={(e) => setLabelConfig(prev => ({ ...prev, showBin: e.target.checked }))}
                  className="w-4 h-4 text-[#2b3380]"
                />
                <label htmlFor="showBin" className="text-sm text-gray-600">Show Bin Location</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showIMEI"
                  checked={labelConfig.showIMEI}
                  onChange={(e) => setLabelConfig(prev => ({ ...prev, showIMEI: e.target.checked }))}
                  className="w-4 h-4 text-[#2b3380]"
                />
                <label htmlFor="showIMEI" className="text-sm text-gray-600">Show IMEI</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showGrade"
                  checked={labelConfig.showGrade}
                  onChange={(e) => setLabelConfig(prev => ({ ...prev, showGrade: e.target.checked }))}
                  className="w-4 h-4 text-[#2b3380]"
                />
                <label htmlFor="showGrade" className="text-sm text-gray-600">Show Grade</label>
              </div>
            </div>
          </div>
        )}

        {/* Actions Bar */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search */}
            <div className="flex-1 relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by DID, brand, model..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2b3380]/20"
              />
            </div>
            
            {/* Selection Actions */}
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Select All ({filteredDevices.length})
              </button>
              {selectedDevices.length > 0 && (
                <>
                  <button
                    onClick={clearSelection}
                    className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    Clear
                  </button>
                  <button
                    onClick={handlePrint}
                    className="px-4 py-2 text-sm font-medium text-white bg-[#2b3380] rounded-lg hover:bg-[#232a66] flex items-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Print {selectedDevices.length} Labels
                  </button>
                </>
              )}
            </div>
            
            {/* View Toggle */}
            <div className="flex border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-[#2b3380] text-white' : 'text-gray-500 hover:bg-gray-50'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-[#2b3380] text-white' : 'text-gray-500 hover:bg-gray-50'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Device Grid/List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2b3380]"></div>
          </div>
        ) : filteredDevices.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <QrCode className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Devices Found</h3>
            <p className="text-gray-500">Complete GRN to generate device labels</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredDevices.map(device => (
              <div
                key={device.device_id}
                onClick={() => toggleDevice(device.device_id)}
                className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${
                  selectedDevices.includes(device.device_id)
                    ? 'border-[#2b3380] ring-2 ring-[#2b3380]/20'
                    : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedDevices.includes(device.device_id)
                      ? 'bg-[#2b3380] border-[#2b3380]'
                      : 'border-gray-300'
                  }`}>
                    {selectedDevices.includes(device.device_id) && (
                      <CheckCircle className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                    {device.pq_grade}
                  </span>
                </div>
                
                {device.qr_code && (
                  <div className="flex justify-center mb-3">
                    <img 
                      src={`data:image/png;base64,${device.qr_code}`} 
                      alt="QR" 
                      className="w-20 h-20"
                    />
                  </div>
                )}
                
                <p className="font-mono text-sm font-semibold text-gray-800 text-center mb-1">
                  {device.device_id}
                </p>
                <p className="text-sm text-gray-600 text-center truncate">
                  {device.brand} {device.model}
                </p>
                {device.bin_id && (
                  <p className="text-xs text-gray-400 text-center mt-1">
                    Bin: {device.bin_id}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Select</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Device ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Model</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Grade</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Bin</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDevices.map(device => (
                  <tr key={device.device_id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedDevices.includes(device.device_id)}
                        onChange={() => toggleDevice(device.device_id)}
                        className="w-4 h-4 text-[#2b3380] rounded"
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-sm">{device.device_id}</td>
                    <td className="px-4 py-3 text-sm">{device.brand} {device.model}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                        {device.pq_grade}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{device.bin_id || '-'}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          setSelectedDevices([device.device_id]);
                          handlePrint();
                        }}
                        className="text-[#2b3380] hover:underline text-sm flex items-center gap-1"
                      >
                        <Printer className="w-4 h-4" />
                        Print
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Hidden Print Container */}
        <div ref={printRef} className="hidden print:block">
          {selectedDevices.map(did => {
            const device = devices.find(d => d.device_id === did);
            return device ? (
              <DeviceLabel key={did} device={device} config={labelConfig} showBorder={false} />
            ) : null;
          })}
        </div>
      </div>
    </Layout>
  );
}

export default DIDLabelPrinting;
